import { forwardRef, useEffect, useState } from 'react'
import styles from './PDFBox.module.less'
import cls from 'classnames'
import PeriodPdfPage from './PeriodPdfPage/PeriodPdfPage'
import OverallPdfPage from './OverallPdfPage/OverallPdfPage'
import HeatMapPdfPage from './HeatMapPdfPage/HeatMapPdfPage'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
import { useTranslation } from 'react-i18next'

const PORTRAIT_HEATMAPS_PER_PAGE = 12
const DEFAULT_HEATMAPS_PER_PAGE = 9

interface Props {
  project: ProjectModels.ProjectDetail
  debug?: boolean
  onProgress: (progress: number, total: number) => void
}

type MomentKey = keyof ProjectModels.ProjectDetail['dimensionMetrics']
export const dimensionMetricsPages = (key: MomentKey, project: ProjectModels.ProjectDetail) => {
  let cnt = 0
  if (key === 'firstThree') {
    if (project.dimensionMetrics.firstThree.reason.performance) {
      cnt += 1
    }
    if (
      project.dimensionMetrics.firstThree.reason.advantage &&
      project.dimensionMetrics.firstThree.reason.disadvantage
    ) {
      cnt += 1
    }
    if (project.dimensionMetrics.firstThree.reason.suggestions) {
      cnt += 1
    }
    return cnt
  }
  if (key === 'lastThree') {
    if (project.dimensionMetrics.lastThree.reason.performance) {
      cnt += 1
    }
    if (
      project.dimensionMetrics.lastThree.reason.advantage &&
      project.dimensionMetrics.lastThree.reason.disadvantage
    ) {
      cnt += 1
    }
    if (project.dimensionMetrics.lastThree.reason.suggestions) {
      cnt += 1
    }
    return cnt
  }
  const dimensionMetrics = project.dimensionMetrics[key]
  if (dimensionMetrics.overall.score > 0) {
    if (dimensionMetrics.overall.reason.performance) {
      cnt += 1
    }
    if (dimensionMetrics.overall.reason.advantage && dimensionMetrics.overall.reason.disadvantage) {
      cnt += 1
    }
    if (dimensionMetrics.overall.reason.suggestions) {
      cnt += 1
    }
  }
  return cnt
}

const genTotalCnt = (project: ProjectModels.ProjectDetail) => {
  /** 介绍 2 页 */
  const introductionPages = 2
  /** 基础信息：封面 + 基础信息 2 页 */
  const part1Pages = 2
  /** 总览: 封面 + 柱状图 + 折线图 + Summary + advantag/disAdvantage + Suggestions */
  const part2Pages = 1 + 5
  /** 时刻分析: 封面 +  时刻片段 + ( Summary + Advantage/Disadvantage + Suggestions ) * 切片的数量 */
  const part3Pages =
    1 +
    dimensionMetricsPages('firstThree', project) +
    dimensionMetricsPages('keyInformation', project) +
    dimensionMetricsPages('brandConnection', project) +
    dimensionMetricsPages('interestingEnagement', project) +
    dimensionMetricsPages('lastThree', project)
  const heatMapsPerPage = isPortraitByProjectInfo(project)
    ? PORTRAIT_HEATMAPS_PER_PAGE
    : DEFAULT_HEATMAPS_PER_PAGE
  /** 热力图: 封面 + 图片 + 结尾 */
  const part4Pages = 1 + Math.ceil(project.frameInfos.length / heatMapsPerPage) + 1
  /** 总的页数 */
  return {
    introCnt: introductionPages,
    part1Cnt: part1Pages,
    part2Cnt: part2Pages,
    part3Cnt: part3Pages,
    part4Cnt: part4Pages,
    total: introductionPages + part1Pages + part2Pages + part3Pages + part4Pages,
  }
}

const PDFBox = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { i18n } = useTranslation()
  const { total, introCnt, part1Cnt, part2Cnt, part3Cnt } = genTotalCnt(props.project)
  const [overallCnt, setOverallCnt] = useState(0)
  const [periodCnt, setPeriodCnt] = useState(0)
  const [heatMapCnt, setHeatMapCnt] = useState(0)

  useEffect(() => {
    props.onProgress(Math.floor((overallCnt + periodCnt + heatMapCnt) / total), total)
  }, [overallCnt, periodCnt, heatMapCnt])

  return (
    <div className={cls(styles.pdfBox, styles[i18n.language], { [styles.debug]: props.debug })} ref={ref}>
      <OverallPdfPage
        baseNo={0}
        project={props.project}
        onRendered={(cnt) => {
          setOverallCnt(cnt)
        }}
      />
      <PeriodPdfPage
        baseNo={introCnt + part1Cnt + part2Cnt}
        project={props.project}
        onRendered={(cnt) => {
          setPeriodCnt(cnt)
        }}
      />
      <HeatMapPdfPage
        baseNo={introCnt + part1Cnt + part2Cnt + part3Cnt}
        project={props.project}
        onRendered={(cnt) => {
          setHeatMapCnt(cnt)
        }}
      />
    </div>
  )
})

export default PDFBox
